import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import DataTable, {TableColumn} from "react-data-table-component";
import {User} from "../models/User";
import {UserService} from "../services/UserService";
import {Badge} from "../components/badge";
import {Button} from "../components/button";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,} from "../components/dialog";
import {Delete, Edit, Plus} from "lucide-react";
import {Input} from "../components/input";
import {toast} from "sonner";

interface AdminDashboardProps {
    user: User;
}

function AdminDashboard({user}: AdminDashboardProps) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isModifyMemberOpen, setIsModifyMemberOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState<User | null>(null);
    const [formData, setFormData] = useState<Partial<User>>({});

    const fetchUsers = async () => {
        try {
            const data = await UserService.getUsers();
            setUsers(data);
            setFilteredUsers(data);
            setLoading(false);
        } catch (error) {
            console.error("Erreur lors de la récupération des utilisateurs", error);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = users.filter(
            (u) =>
                u.email.toLowerCase().includes(lowerSearch) ||
                u.firstName.toLowerCase().includes(lowerSearch) ||
                u.lastName.toLowerCase().includes(lowerSearch) ||
                u.role.toLowerCase().includes(lowerSearch)
        );
        setFilteredUsers(filtered);
    }, [searchTerm, users]);

    const openModifyDialog = (user: User) => {
        setSelectedMember(user);
        setFormData({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phoneNumber: user.phoneNumber,
            role: user.role,
        });
        setIsModifyMemberOpen(true);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
    };

    const handleModifyMember = async () => {
        if (!selectedMember) return;
        try {
            await UserService.updateUser(selectedMember.id, formData);
            await fetchUsers();
            setIsModifyMemberOpen(false);
            toast.success("Utilisateur mis à jour avec succès !");
        } catch (error) {
            toast.error("Erreur lors de la mise à jour de l'utilisateur.");
        }
    };

    const handleDeleteMember = async (userId: number) => {
        try {
            await UserService.deleteUser(userId);
            await fetchUsers();
            toast.success("Utilisateur supprimé avec succès !");
        } catch (error) {
            toast.error("Erreur lors de la suppression de l'utilisateur.");
        }
    }

    const columns: TableColumn<User>[] = [
        {name: "ID", selector: (row) => row.id, sortable: true, width: "80px"},
        {name: "Email", selector: (row) => row.email, sortable: true},
        {name: "First Name", selector: (row) => row.firstName, sortable: true},
        {name: "Last Name", selector: (row) => row.lastName, sortable: true},
        {name: "Phone", selector: (row) => row.phoneNumber || "-", sortable: true},
        {
            name: "Role",
            cell: (row) => (
                <Badge
                    variant={
                        row.role === "ADMIN"
                            ? "destructive"
                            : row.role === "MANAGER"
                                ? "default"
                                : "secondary"
                    }
                >
                    {row.role}
                </Badge>
            ),
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openModifyDialog(row)}
                    >
                        <Edit className="mr-2 h-4 w-4"/> Modifier
                    </Button>
                    <Button
                        style={{backgroundColor: "#ff4d4f", borderColor: "transparent", color: "white"}}
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteMember(row.id)}
                    >
                        <Delete className="mr-2 h-4 w-4"/> Supprimer
                    </Button>
                </div>
            ),
            right: true,
        },
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Dashboard Admin</h1>
                    <p className="text-muted-foreground">
                        Vue d'ensemble des utilisateurs de la plateforme.
                    </p>
                </div>
                <Button className="gap-2" onClick={() => navigate("/register")}>
                    <Plus className="h-4 w-4"/>
                    Ajouter un employé
                </Button>
            </div>

            <div>
                <input
                    type="text"
                    placeholder="Rechercher un utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
                />

                <DataTable
                    columns={columns}
                    data={filteredUsers}
                    pagination
                    highlightOnHover
                    striped
                    persistTableHead
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[5, 10, 20, 50]}
                    noDataComponent="Aucun utilisateur trouvé."
                />
            </div>

            <Dialog
                open={isModifyMemberOpen}
                onOpenChange={(open) => setIsModifyMemberOpen(open)}
            >
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Modifier l'utilisateur</DialogTitle>
                        <DialogDescription>
                            Modifiez les informations de l'utilisateur sélectionné.
                        </DialogDescription>
                    </DialogHeader>

                    {selectedMember && (
                        <div className="space-y-3 py-4">
                            <Input
                                name="firstName"
                                placeholder="Prénom"
                                value={formData.firstName || ""}
                                onChange={handleChange}
                            />
                            <Input
                                name="lastName"
                                placeholder="Nom"
                                value={formData.lastName || ""}
                                onChange={handleChange}
                            />
                            <Input
                                name="email"
                                placeholder="Email"
                                type="email"
                                value={formData.email || ""}
                                onChange={handleChange}
                            />
                            <Input
                                name="phoneNumber"
                                placeholder="Téléphone"
                                value={formData.phoneNumber || ""}
                                onChange={handleChange}
                            />
                            <Input
                                name="role"
                                placeholder="Rôle (ADMIN / MANAGER / EMPLOYEE)"
                                value={formData.role || ""}
                                onChange={handleChange}
                            />
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsModifyMemberOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleModifyMember}>Enregistrer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default AdminDashboard;
